import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  FootballManagerTaskState,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { ArenaTicket } from '@sorare/core/src/atoms/icons/rivals/branded/ArenaTicket';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import Scrollable from '@sorare/core/src/atoms/navigation/Scrollable';
import {
  HeadlineL,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { glossary } from '@sorare/core/src/lib/glossary';
import { Color } from '@sorare/core/src/style/types';

import { RewardBoxDialog } from '@sorare/gameplay/src/components/rewards/RewardBoxDialog';
import { useClaimManagerTask } from '@sorare/gameplay/src/hooks/useClaimManagerTask';

import {
  DailyReward,
  DailyRewardBlock,
  FullSpaceButton,
  RewardWrapper,
} from 'components/rivals/DailyClaimDialog/DailyReward';

import { FirstWeekClaim_footballManagerTask } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: 0 var(--triple-unit);
  max-width: 100%;
`;
const DailyRewardWrapper = styled.div`
  padding: var(--unit) 0;
`;
const BoxQuality = styled(LabelM)`
  text-transform: capitalize;
`;

type rewardProp = {
  bgColor: Color;
  borderColor?: Color;
};
const rewardProps = ({
  aasmState,
  rewardConfig,
}: FirstWeekClaim_footballManagerTask): rewardProp => {
  const isBox = !!rewardConfig.probabilisticBundleConfiguration;

  switch (aasmState) {
    case FootballManagerTaskState.COMPLETED: {
      return { bgColor: 'var(--c-brand-600)' };
    }
    case FootballManagerTaskState.CANCELED: {
      return {
        bgColor: 'rgba(var(--c-rgb-red-300), 0.15)',
        borderColor: 'rgba(var(--c-rgb-red-300), 0.15)',
      };
    }
    case FootballManagerTaskState.CLAIMED: {
      return {
        bgColor: 'rgba(var(--c-rgb-green-800), 0.15)',
        borderColor: 'rgba(var(--c-rgb-green-800), 0.15)',
      };
    }
    case FootballManagerTaskState.READY: {
      return isBox
        ? {
            bgColor: 'rgba(var(--c-rgb-rivals-friendlies), 0.25)',
            borderColor: 'rgba(var(--c-rgb-rivals-friendlies), 0.25)',
          }
        : {
            bgColor: 'var(--c-neutral-400)',
            borderColor: 'var(--c-neutral-400)',
          };
    }
    default: {
      return {
        bgColor: 'var(--c-neutral-400)',
        borderColor: 'var(--c-neutral-400)',
      };
    }
  }
};

type Props = { dailyClaimTasks: FirstWeekClaim_footballManagerTask[] };
export const FirstWeekClaim = ({ dailyClaimTasks }: Props) => {
  const [boxOpened, setBoxOpened] = useState(false);
  const [claim, { loading }] = useClaimManagerTask();

  const onClaim = async (footballManagerTaskId: string) => {
    await claim({
      variables: {
        sport: Sport.FOOTBALL,
        input: {
          footballManagerTaskId,
        },
      },
    });
    setBoxOpened(true);
  };

  const readyTask = dailyClaimTasks.find(
    ({ aasmState }) => aasmState === FootballManagerTaskState.READY
  );

  return (
    <Root gap={3} center>
      <Vertical center>
        <HeadlineL as="h2">
          <FormattedMessage
            id="ClaimArenaTicketDialog.title"
            defaultMessage="Daily Claim"
          />
        </HeadlineL>
        <LabelM as="span" color="var(--c-neutral-600)">
          <FormattedMessage
            id="ClaimArenaTicket.firstWeekClaims.description"
            defaultMessage="Free claim everyday the first 7 days"
          />
        </LabelM>
      </Vertical>
      <Scrollable
        itemToDisplay={2}
        indexToScroll={dailyClaimTasks.findIndex(
          ({ aasmState }) => aasmState !== FootballManagerTaskState.CLAIMED
        )}
        withMask
      >
        {dailyClaimTasks.map((task, i) => {
          const isCompleted =
            task.aasmState === FootballManagerTaskState.COMPLETED;
          const isLocked =
            task.aasmState === FootballManagerTaskState.ASSIGNED ||
            (task.aasmState === FootballManagerTaskState.READY &&
              readyTask?.id !== task.id);

          const { borderColor, bgColor } = rewardProps(task);

          return (
            <DailyRewardWrapper key={task.id}>
              <DailyReward
                isBox={!!task.rewardConfig.probabilisticBundleConfiguration}
                disabled={isLocked}
                readyToClaim={isCompleted}
                backgroundColor={bgColor}
                borderColor={borderColor}
                footer={
                  <>
                    {task.aasmState === FootballManagerTaskState.READY &&
                      readyTask?.id === task.id && (
                        <>
                          <LabelM as="span" color="var(--c-neutral-1000)" bold>
                            <FormattedMessage
                              id="ClaimArenaTicketDialog.FirstWeekClaim.wait"
                              defaultMessage="Wait"
                            />
                          </LabelM>
                          <LabelS as="span">
                            {task.readyAt && <TimeLeft time={task.readyAt} />}
                          </LabelS>
                        </>
                      )}
                    {task.aasmState === FootballManagerTaskState.CLAIMED && (
                      <LabelL as="span" color="var(--c-green-800)" bold>
                        <FormattedMessage {...glossary.claimed} />
                      </LabelL>
                    )}
                    {task.aasmState === FootballManagerTaskState.CANCELED && (
                      <LabelL as="span" color="var(--c-red-600)" bold>
                        <FormattedMessage {...glossary.unclaimed} />
                      </LabelL>
                    )}
                    {task.aasmState === FootballManagerTaskState.COMPLETED && (
                      <FullSpaceButton
                        onClick={() => {
                          onClaim(task.id);
                        }}
                      >
                        {loading ? (
                          <LoadingIndicator smaller />
                        ) : (
                          <LabelL as="span" bold>
                            <FormattedMessage {...glossary.claimNow} />
                          </LabelL>
                        )}
                      </FullSpaceButton>
                    )}
                    {isLocked && (
                      <LabelL as="span" color="var(--c-neutral-600)" bold>
                        <FormattedMessage
                          id="ClaimArenaTicketDialog.dailyClaimTasks.dayI"
                          defaultMessage="Day {i}"
                          values={{ i: i + 1 }}
                        />
                      </LabelL>
                    )}
                  </>
                }
              >
                <DailyRewardBlock>
                  {(task.rewardConfig.arenaTicketAmount || 0) > 0 && (
                    <>
                      <RewardWrapper>
                        <ArenaTicket width={20} double />
                      </RewardWrapper>
                      <LabelM color="var(--c-neutral-1000)" as="span" bold>
                        {task.rewardConfig.arenaTicketAmount}
                      </LabelM>
                    </>
                  )}
                  {task.rewardConfig.probabilisticBundleConfiguration && (
                    <>
                      <RewardWrapper>
                        <img
                          width={36}
                          height={36}
                          src={
                            task.rewardConfig.probabilisticBundleConfiguration
                              .pictureUrl
                          }
                          alt=""
                        />
                      </RewardWrapper>
                      <BoxQuality color="var(--c-neutral-1000)" as="span" bold>
                        {task.rewardConfig.probabilisticBundleConfiguration.quality.toLowerCase()}
                      </BoxQuality>
                    </>
                  )}
                  {task.probabilisticBundle && (
                    <RewardBoxDialog
                      rewardBoxes={[task.probabilisticBundle]}
                      open={boxOpened}
                      onClose={() => setBoxOpened(false)}
                    />
                  )}
                </DailyRewardBlock>
              </DailyReward>
            </DailyRewardWrapper>
          );
        })}
      </Scrollable>
    </Root>
  );
};

FirstWeekClaim.fragments = {
  footballManagerTask: gql`
    fragment FirstWeekClaim_footballManagerTask on FootballManagerTask {
      id
      aasmState
      label
      readyAt
      rewardConfig {
        arenaTicketAmount
        coinAmount
        probabilisticBundleConfiguration {
          id
          pictureUrl
          quality
        }
      }
      probabilisticBundle {
        id
        ...RewardBoxDialog_ProbabilisticBundle
      }
    }
    ${RewardBoxDialog.fragments.ProbabilisticBundle}
  ` as TypedDocumentNode<FirstWeekClaim_footballManagerTask>,
};
